import type { Table } from '@tanstack/react-table';

import type { ExportFormat, ExportResult } from '../../types/export';
import { EXPORT_FORMAT_MIME_TYPES } from '../../constants/export';
import { generateCSV } from './csv';
import { downloadFile, downloadFromURL } from './download';
import { generateJSON } from './json';
import { getExportHeaders, getExportRows } from './table-export';
import { generateXLSX } from './xlsx';

export type ExecuteClientExportOptions<TData> = {
  readonly table: Table<TData>;
  readonly columnIds: string[];
  readonly format: ExportFormat;
  readonly filename: string;
};

/**
 * Executes a client-side export by generating and downloading a file.
 *
 * Async only because the xlsx writer loads exceljs on demand; the csv and json paths resolve
 * without yielding.
 */
export async function executeClientExport<TData>({
  table,
  columnIds,
  format,
  filename,
}: ExecuteClientExportOptions<TData>): Promise<void> {
  const headers = getExportHeaders(table, columnIds);
  const rows = getExportRows(table, columnIds);

  let content: string | Blob;

  switch (format) {
    case 'csv':
      content = generateCSV(headers, rows, columnIds);
      break;
    case 'json':
      content = generateJSON(rows, columnIds);
      break;
    case 'xlsx':
      content = await generateXLSX(headers, rows, columnIds);
      break;
    default:
      throw new Error('Unsupported format');
  }

  downloadFile(content, filename, EXPORT_FORMAT_MIME_TYPES[format]);
}

/**
 * Handles an export result from server-side export.
 */
export function handleExportResult(result: ExportResult): void {
  if ('url' in result) {
    downloadFromURL(result.url, result.filename);
  } else {
    downloadFile(result.blob, result.filename, result.mimeType);
  }
}
